import type {
  Credit,
  FinanceProvider,
  FinanceRate,
  Job,
  Lead,
} from '@kanda-libs/ks-frontend-services';

import { getDepositLimit } from 'pages/Quote/helpers';
import type { FormattedJob } from 'types';

import { calculateJobTotal } from './Jobs-functions';

import { PROVIDERS } from '../constants';

/**
 * @typedef {Object} FinanceOptions
 * @property {Number} interest - The interest on the finance option
 * @property {Number} duration - The duration of the finance option in months
 */

interface FinaceOptionDetails {
  interest: number;
  duration: number;
  deferred?: number;
}

/**
 * Converts finance option name into interest and duration
 * @param {String} financeOption finance option name, e.g. INTEREST_BEARING_60_1190
 *
 * @returns {FinanceOptions} Finance options object
 */
export const getFinanceOptionDetails = (financeOption): FinaceOptionDetails => {
  if (financeOption.toLowerCase().includes('free'))
    return {
      interest: 0,
      duration: parseInt(financeOption.split('_').slice(-1)[0], 10),
    };

  if (!financeOption.toLowerCase().includes('buynow_paylater')) {
    const [durationString, interestString] = financeOption.split('_').slice(-2);
    return {
      interest: parseInt(interestString, 10) / 100,
      duration: parseInt(durationString, 10),
    };
  }
  const [deferredDurationString, durationString, interestString] = financeOption
    .split('_')
    .slice(-3);

  return {
    interest: parseInt(interestString, 10) / 100,
    duration: parseInt(durationString, 10),
    deferred: parseInt(deferredDurationString, 10),
  };
};

export const calculateBaseFee = (financeOption) => {
  const { interest, duration } = getFinanceOptionDetails(financeOption);
  if (interest === 0 && duration === 10) return 0;
  if (interest === 11.9) return 4900;
  return 2800;
};

/**
 * Calculates cost to tradesperson from finance breokerage fee
 */
export const calculateCost = (lineItems, fee, financeOption) => {
  if (!lineItems || !financeOption || lineItems.length === 0) return 0;

  const { totalIncVat: total } = calculateJobTotal(lineItems);

  const percentage = fee / 100;

  const baseRate = (total * percentage) / 100;

  const baseFee = calculateBaseFee(financeOption);

  return {
    percentage,
    baseRate,
    baseFee,
    total: baseRate + baseFee,
  };
};

/**
 * Calculates Loan from total and payment method
 * @param {Number} total total amount
 * @param {Number} paymentMethod payment method
 * @returns {Number} base fee
 */
export const calculateLoanCosts = (totals, paymentMethod, deposit = 0) => {
  if (!paymentMethod || !totals)
    return {
      monthly: 0,
      financeCost: 0,
      total: 0,
    };

  const total =
    deposit || deposit === 0
      ? totals.totalIncVat - deposit
      : Math.ceil(totals.totalIncVat * 0.9);

  const { interest, duration } = getFinanceOptionDetails(paymentMethod);

  const rate = interest / 12 / 100;

  if (rate === 0)
    return {
      monthly: Math.ceil(total / duration),
      financeCost: 0,
      total,
    };

  const compoundInterest = (1 + rate) ** duration;

  const monthlyLoan =
    (total * (rate * compoundInterest)) / (compoundInterest - 1);

  const cost = Math.ceil(monthlyLoan * duration);

  return {
    monthly: Math.ceil(monthlyLoan),
    financeCost: cost - total,
    total: cost,
  };
};

export const constructFinancePlan = (plan) => {
  if (!plan) return '';
  const { rate, term } = plan;
  if (parseInt(rate, 10) === 0) return `INTEREST_FREE_${term}`;
  return `INTEREST_BEARING_${term}_${Math.round(rate * 100)}`;
};

export const calculateDepositFromPercentage = (
  percentage,
  total,
  // minPercentage = 10,
  maxPercentage = 50,
) => {
  const calculatedDeposit = Math.ceil((parseInt(percentage, 10) * total) / 100);
  const maxDeposit =
    (parseInt(maxPercentage as unknown as string, 10) * total) / 100;
  if (calculatedDeposit > maxDeposit) return calculatedDeposit - 1;
  return calculatedDeposit;
};

// Takes an error message formatted as:
// "code=400, message=Error message"
// and returns "400"
// @param {String} errorMessage - error message
// @returns {Boolean} error code
export const getErrorCode = (error?: string) => {
  if (!error) return '';
  const [initialParam] = error.split(',');
  const [, code] = initialParam.split('code=');
  return code ? parseInt(code, 10) : null;
};

export const getLoanProvider = (job) => {
  if (!job?.payments) return 'omni';

  const loan = job?.payments
    ?.filter((p) => p?.payment_option.payment_method === 'loan')
    ?.sort(
      (p1, p2) =>
        (new Date(p2?.metadata?.created_at) as unknown as number) -
        (new Date(p1?.metadata?.created_at) as unknown as number),
    )?.[0];

  return loan?.payment_option?.provider || 'omni';
};

export const formatToCurrency = (
  value: number,
  currency = 'GBP',
  locale = 'en-US',
  minorUnit = 2,
): string => {
  const majorUnitValue = value / 10 ** minorUnit;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnit,
  });
  return formatter.format(majorUnitValue);
};

export const formatToPercentage = (value: number): string => {
  if (value === 0) return '0%';
  if (value % 100 === 0) return `${value / 100}%`;
  const str = value.toString();
  const major = str.substring(0, str.length - 2);
  const decimals = str.substring(str.length - 2);
  if (value % 10 === 0) return `${major}.${decimals[0]}%`;
  return `${major}.${decimals}%`;
};

export const getPaymentMethod = (interest: number, duration: string) => {
  const prefix = interest === 0 ? 'INTEREST_FREE_' : 'INTEREST_BEARING_';
  const suffix = interest === 0 ? '' : `_${Math.round(interest * 100)}`;
  return [prefix, duration, suffix].filter((str): string => str).join('');
};

export const canApplySecondLine = (job: Job | FormattedJob) => {
  const financeOptions = job?.finance_options;
  if (!financeOptions) return false;
  return financeOptions.some((rate: FinanceRate) => rate.enabled === false);
};

export const hasAppliedSecondLine = (
  job: Job | FormattedJob,
  credit?: Credit,
) => {
  const financeOptions = job?.finance_options;
  const financeStatus = job?.finance_status;
  const canApply = canApplySecondLine(job);
  if (!financeOptions || !canApply) return false;
  const latestLine = credit?.finance_option?.finance_types[0];
  if (latestLine !== 'secondary') return false;
  if (financeStatus === 'not_applied') return false;
  return true;
};

export const getProvider = (credit: Credit | undefined) => {
  if (!credit) return PROVIDERS.unknown;
  const provider = credit?.provider || 'unknown';
  return PROVIDERS[provider];
};

export const formatProvider = (provider: FinanceProvider) =>
  PROVIDERS[provider];

export interface LowestMonthlyRate {
  monthlyCost: number;
  financeOption?: FinanceRate;
  jobTotalForMinimumMonthlyCost: number;
}

export const getLowestMonthlyRate = (
  job: Job,
  lead?: Lead,
  financeOptions: FinanceRate[] = [],
): LowestMonthlyRate => {
  const total = job?.total?.amount_vat_inclusive?.amount || 0;

  const options = financeOptions
    .filter((rate) => !rate.finance_types.includes('secondary') && rate.enabled)
    .map((option: FinanceRate) => {
      // We get the maximum deposit, to work out the minimum monthly cost
      const depositLimits = getDepositLimit(option?.name, job, lead);
      const maxDeposit = parseInt(depositLimits.max, 10);

      return {
        option,
        cost: calculateLoanCosts(
          { totalIncVat: total },
          option.name,
          maxDeposit,
        ),
      };
    })
    .filter((option) => !Number.isNaN(option.cost.monthly));

  const monthlyCosts = options.map((option) => option.cost.monthly);

  const monthlyCost = Math.min(...monthlyCosts) || 0;

  const financeOption = options.find(
    (option) => option.cost.monthly === monthlyCost,
  )?.option;

  const financeOptionDepositLimit = getDepositLimit(
    financeOption?.name,
    job,
    lead,
  );
  const maxDeposit = parseInt(financeOptionDepositLimit.max, 10);

  const jobTotalForMinimumMonthlyCost = total - maxDeposit;

  return {
    monthlyCost,
    financeOption,
    jobTotalForMinimumMonthlyCost,
  };
};

export const getLenderName = (provider: FinanceProvider | undefined) => {
  if (!provider) return 'our lender';
  if (provider === 'allium') return 'Tandem';
  if (provider === 'etika') return 'etika';
  if (provider === 'humm') return 'Humm';
  if (provider === 'novuna') return 'Novuna';
  if (provider === 'omni') return 'Omni';
  if (provider === 'propensio') return 'Propensio';
  return 'our lender';
};
